import { useId, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useReadModel } from "../data/useReadModel";
import { fetchResponseOnlySignals, type ResponseOnlySignal } from "../data/sos-response-only";
import {
  acknowledgeSosSignal, standDownSosAlert, STAND_DOWN_NOTE_MAX, STAND_DOWN_OUTCOMES,
  SOS_ACTION_UNAVAILABLE, SOS_QUEUE_EMPTY, SOS_QUEUE_CAPPED_DETAIL, SOS_QUEUE_UNAVAILABLE,
  standDownOutcomeLabel, type StandDownOutcomeKey,
} from "../data/sos-desk";

export function SosResponseOnlyPanel({ client, accountId, contextKey, supportActive, policyState, pollMs, selectedAlertEventId }: {
  client: SupabaseClient; accountId: string; contextKey: string; supportActive: boolean;
  policyState: "loading" | "paused" | "unavailable"; pollMs?: number; selectedAlertEventId?: string | null;
}) {
  const read = useReadModel(
    async () => ({ contextKey, queue: await fetchResponseOnlySignals(client, accountId) }),
    [client, contextKey], pollMs,
  );
  const current = read.data?.contextKey === contextKey ? read.data.queue : null;
  return <section aria-label="Existing SOS response">
    <h2 className="sos__title">Existing SOS response</h2>
    <p role="status">{policyState === "paused"
      ? "New SOS creation is paused. Existing alerts remain available for response."
      : policyState === "loading" ? "Checking SOS creation policy. Existing-alert response remains available."
      : "SOS creation policy is unavailable. Only existing-alert response is shown."}</p>
    <p>Use your site emergency process for new emergencies. SecOps does not contact emergency services.</p>
    {supportActive ? <p role="status">Support session · Read-only SOS response</p> : null}
    {current === null ? <p role={read.status === "error" ? "alert" : "status"}>
      {read.status === "error" ? SOS_QUEUE_UNAVAILABLE : "Loading existing SOS signals…"}
    </p> : current.status === "unavailable" ? <p role="alert">
      {current.reason === "capped" ? SOS_QUEUE_CAPPED_DETAIL : SOS_QUEUE_UNAVAILABLE}
    </p> : current.status === "empty" ? <p role="status">{SOS_QUEUE_EMPTY}</p> : <>
      {read.status === "error" ? <p role="alert">Could not refresh existing SOS signals. Actions are unavailable until a fresh read succeeds.</p> : null}
      {selectedAlertEventId && !current.rows.some(row => row.alertEventId === selectedAlertEventId)
        ? <p role="status">The selected alert is not in this readable window.</p> : null}
      {current.rows.map(row => <ResponseCard key={row.alertEventId} signal={row} client={client}
        accountId={accountId} canWrite={!supportActive && read.status === "ready"} onReviewed={read.reload} />)}
    </>}
  </section>;
}

function ResponseCard({ signal, client, accountId, canWrite, onReviewed }: {
  signal: ResponseOnlySignal; client: SupabaseClient; accountId: string; canWrite: boolean; onReviewed: () => void;
}) {
  const noteId = useId();
  const [outcome, setOutcome] = useState<StandDownOutcomeKey | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const open = signal.status === "open" || signal.status === "acknowledged";
  async function act(kind: "acknowledge" | "stand_down") {
    if (!canWrite || !open || busy) return;
    setBusy(true); setMessage(null);
    try {
      const result = kind === "acknowledge"
        ? await acknowledgeSosSignal(client, accountId, signal.alertEventId)
        : await standDownSosAlert(client, accountId, signal.alertEventId, outcome, note);
      if (result.ok) {
        setMessage(kind === "acknowledge" ? "Acknowledgement recorded." : "Stand-down recorded.");
        setOutcome(null); setNote(""); onReviewed();
      } else setMessage(result.reason ?? SOS_ACTION_UNAVAILABLE);
    } catch { setMessage(SOS_ACTION_UNAVAILABLE); }
    finally { setBusy(false); }
  }
  return <article className="sos__card" aria-label="Existing SOS signal">
    <h3>{signal.guardLabel ?? "Officer"}</h3>
    <p>{[signal.siteName, signal.routeName].filter(Boolean).join(" · ") || "Post unavailable"}</p>
    <p>{signal.status === "acknowledged" ? "Dispatch responding" : signal.status === "open" ? "Open SOS" : "Closed SOS"}</p>
    <p>{signal.responseCount} recorded acknowledgement{signal.responseCount === 1 ? "" : "s"}.</p>
    {signal.callerAcknowledged ? <p>You acknowledged this signal.</p> : null}
    {signal.standDownOutcome ? <p>{standDownOutcomeLabel(signal.standDownOutcome)}</p> : null}
    {canWrite && open ? <>
      {!signal.callerAcknowledged ? <button type="button" disabled={busy} onClick={() => void act("acknowledge")}>Acknowledge existing SOS</button> : null}
      <label>Stand-down outcome
        <select value={outcome ?? ""} disabled={busy} onChange={event => setOutcome(event.target.value === "" ? null : event.target.value as StandDownOutcomeKey)}>
          <option value="">Choose what happened</option>
          {STAND_DOWN_OUTCOMES.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
        </select>
      </label>
      <label htmlFor={noteId}>Stand-down note (optional)</label>
      <textarea id={noteId} value={note} maxLength={STAND_DOWN_NOTE_MAX} disabled={busy} onChange={event => setNote(event.target.value)} />
      <button type="button" disabled={busy || outcome === null} onClick={() => void act("stand_down")}>Stand down existing SOS</button>
    </> : null}
    {message ? <p role="status">{message}</p> : null}
  </article>;
}
